import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export type NotificationChannel = "inApp" | "email" | "slack";

export interface NotificationChannels {
  inApp: boolean;
  email: boolean;
  slack: boolean;
}

export interface UserNotificationPreference {
  id: string;
  publicId: string;
  userId: string;
  category: string;
  channels: NotificationChannels;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  timezone: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface NotificationPreferenceState {
  preferences: Record<string, UserNotificationPreference>;
  isLoading: boolean;
  error: string | null;
}

const initialState: NotificationPreferenceState = {
  preferences: {},
  isLoading: false,
  error: null,
};

// Fetch all preferences
export const fetchPreferences = createAsyncThunk("notificationPreferences/fetch", async () => {
  const response = await makeRequest<{ data: Record<string, UserNotificationPreference> }>(`/v1/notification-preferences`);
  return response.data;
});

// Fetch preference for a category
export const fetchPreferenceByCategory = createAsyncThunk("notificationPreferences/fetchByCategory", async (category: string) => {
  const response = await makeRequest<{ data: UserNotificationPreference }>(`/v1/notification-preferences/${category}`);
  return response.data;
});

// Update preference
export const updatePreference = createAsyncThunk(
  "notificationPreferences/update",
  async ({ category, updates }: { category: string; updates: Partial<UserNotificationPreference> }) => {
    const response = await makeRequest<{ data: UserNotificationPreference }>(`/v1/notification-preferences/${category}`, {
      method: "PUT",
      body: updates,
    });
    return response.data;
  },
);

// Reset all preferences to defaults
export const resetPreferences = createAsyncThunk("notificationPreferences/reset", async () => {
  await makeRequest(`/v1/notification-preferences/reset`, {
    method: "POST",
  });
});

const notificationPreferenceSlice = createSlice({
  name: "notificationPreferences",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all preferences
      .addCase(fetchPreferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch preferences";
      })
      // Fetch by category
      .addCase(fetchPreferenceByCategory.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPreferenceByCategory.fulfilled, (state, action) => {
        state.preferences[action.payload.category] = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchPreferenceByCategory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to fetch preference";
      })
      // Update preference
      .addCase(updatePreference.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePreference.fulfilled, (state, action) => {
        state.preferences[action.payload.category] = action.payload;
        state.isLoading = false;
      })
      .addCase(updatePreference.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to update preference";
      })
      // Reset preferences
      .addCase(resetPreferences.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(resetPreferences.fulfilled, (state) => {
        // Preferences will be fetched again to get defaults
        state.isLoading = false;
      })
      .addCase(resetPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Failed to reset preferences";
      });
  },
});

export const { clearError } = notificationPreferenceSlice.actions;
export default notificationPreferenceSlice.reducer;
